import { getISOWeek } from "@/lib/utils";
import { Priority } from "@/types";
import type { SubmitIntent } from "./form/priority-form-constants";

export function weekYearForPriorityDialog(priority: Priority | null | undefined) {
  if (priority?.weekNumber != null && priority?.weekYear != null) {
    return { week: priority.weekNumber, year: priority.weekYear };
  }
  return getISOWeek(new Date());
}

/**
 * Identity of the assignment state the form was built from. Used as a React `key` so the form remounts —
 * and re-reads its default values — when the roster changes underneath it, e.g. after a claim adds a
 * DRI row. Deliberately only the roster: keying on `updatedAt` would also remount on a teammate's
 * unrelated write and throw away whatever the user had typed.
 */
export function priorityFormKey(priority: Priority | null | undefined): string {
  if (!priority?.pid) return "new";
  const roster = (priority.assignments ?? []).map((a) => `${a.assignedUser.pid}:${a.isDri ? 1 : 0}:${a.status}`).join(",");
  return `${priority.pid}|${roster}`;
}

export interface SubmitAction {
    label: string;
    intent: SubmitIntent;
}

/**
 * Footer buttons for the priority form. New or candidate deliverables offer both saves; a committed
 * one only "Save Changes" — dropping its DRI or PPPs is how it goes back to Candidates.
 *
 * Only the button that literally says "candidate" carries the `candidate` intent, because that intent
 * saves without a DRI. "Save Changes" must never silently drop one.
 */
export function submitLabels(
    priority: Priority | null | undefined,
    isOwnRowOnly: boolean,
): { primary: SubmitAction; secondary?: SubmitAction } {
    if (isOwnRowOnly) return { primary: { label: "Update My Assignment", intent: "save" } };
    if (priority?.pid && !priority.isCandidate) return { primary: { label: "Save Changes", intent: "save" } };
    return {
        secondary: priority?.pid
            ? { label: "Save Changes", intent: "save" }
            // Names the consequence: this save drops the DRI the form is showing.
            : { label: "Save as candidate (no DRI)", intent: "candidate" },
        primary: { label: "Commit", intent: "commit" },
    };
}
